"use client";

import * as React from "react";
import { Check, X , Search} from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";

// Import the assets service
import { getAssets } from '@/services/assetsService';
import { useTenancy } from '@/contexts/TenancyContext';

// Local type for the combobox
interface Asset {
  id: string;
  name: string;
  type?: string;
  description?: string;
  originalAsset: any; // Original asset data from the service
}

// Props for the AssetsCombobox component
interface AssetsComboboxProps {
  selected: string[];
  setSelected: (values: string[] | ((prev: string[]) => string[])) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onAssetSelect?: (assetId: string) => void;
  onAssetDeselect?: (assetId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  assets?: Asset[]; // Optionally pass assets directly
  fetchAssets?: () => Promise<Asset[]>; // Or provide a function to fetch them
}

// Create a context to provide assets data
interface AssetsContextType {
  assets: Asset[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Asset[]>;
  typeFilters: string[];
  searchAssets: (query: string, typeFilter?: string) => Promise<void>;
}

const AssetsContext = React.createContext<AssetsContextType>({
  assets: [],
  loading: false,
  error: null,
  refresh: async () => [],
  typeFilters: [],
  searchAssets: async () => {},
});

// Provider component to fetch and manage assets data
interface AssetsProviderProps {
  children: React.ReactNode;
}

export const AssetsProvider: React.FC<AssetsProviderProps> = ({ children }) => {
  const [assets, setAssets] = React.useState<Asset[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [typeFilters, setTypeFilters] = React.useState<string[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<string | null>(null);
  const { activeTenancy } = useTenancy();

  // Simple logger function
  const log = (message: string, data?: any, level: 'log' | 'warn' | 'error' = 'log') => {
    const styles = 'color: #4CAF50; font-weight: bold;';
    const logMessage = `%c[AssetsProvider] ${message}`;
    
    if (level === 'error') {
      console.error(logMessage, styles, data || '');
    } else if (level === 'warn') {
      console.warn(logMessage, styles, data || '');
    } else {
      console.log(logMessage, styles, data || '');
    }
  };

  // Store all assets in ref to avoid refetching
  const allAssetsRef = React.useRef<Asset[]>([]);

  // Filter assets based on query and type
  const filterAssets = React.useCallback((query: string = '', typeFilter: string | null = null) => {
    let filtered = [...allAssetsRef.current];
    
    if (query) {
      const queryLower = query.toLowerCase();
      filtered = filtered.filter(
        asset => 
          asset.name.toLowerCase().includes(queryLower) ||
          (asset.description && asset.description.toLowerCase().includes(queryLower)) ||
          (asset.type && asset.type.toLowerCase().includes(queryLower))
      );
    }
    
    if (typeFilter) {
      filtered = filtered.filter(asset => asset.type === typeFilter);
    }
    
    setAssets(filtered);
    log('Applied filters', { query, typeFilter, count: filtered.length });
  }, []);

  // Fetch all assets once and filter in-memory
  const fetchAllAssets = React.useCallback(async () => {
    if (!activeTenancy?.id || activeTenancy.id === 'Loading...' || initialized) return;
    
    try {
      setLoading(true);
      log('Fetching all assets...');
      
      const assetsData = await getAssets();
      
          // Transform and store all assets
      allAssetsRef.current = assetsData.map((asset: any) => ({
        id: String(asset.assetID ?? ''), // Ensure ID is a string
        name: asset.assetDesc || asset.assetName || 'Unnamed Asset',
        type: asset.assetType || 'Other',
        description: asset.assetDescription || '',
        originalAsset: asset
      }));
      
      // Extract unique types for filters
      const types = Array.from(
        new Set(allAssetsRef.current.map(a => a.type).filter(Boolean))
      ) as string[];
      
      setTypeFilters(types);
      setInitialized(true);
      log('All assets loaded', { assets: allAssetsRef.current, count: allAssetsRef.current.length });
      
      // Apply any active filters
      filterAssets(searchQuery, activeFilter);
    } catch (error) {
      log('Error fetching assets', error, 'error');
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, initialized, searchQuery, activeFilter, filterAssets]);
  
  // Initial fetch on mount
  React.useEffect(() => {
    fetchAllAssets();
  }, [fetchAllAssets]);
  
  // Handle search and filter changes
  const searchAssets = React.useCallback(async (query: string = '', typeFilter?: string): Promise<void> => {
    setSearchQuery(query);
    const filter = typeFilter || null;
    setActiveFilter(filter);
    filterAssets(query, filter);
    return Promise.resolve();
  }, [filterAssets]);
  
  // Handle search query changes with debounce
  React.useEffect(() => {
    const debounceTimer = setTimeout(() => {
      filterAssets(searchQuery, activeFilter);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, activeFilter, filterAssets]);
  
  // Refresh function to manually refresh assets
  const refresh = async () => {
    await fetchAllAssets();
    return allAssetsRef.current;
  };
  
  return (
    <AssetsContext.Provider
      value={{
        assets,
        loading,
        error,
        refresh,
        typeFilters,
        searchAssets,
      }}
    >
      {children}
    </AssetsContext.Provider>
  );
};

// Hook to use the assets context
export const useAssets = (): AssetsContextType => {
  const context = React.useContext(AssetsContext);
  if (!context) {
    throw new Error('useAssets must be used within an AssetsProvider');
  }
  return context;
};

// Main AssetsCombobox component
export const AssetsCombobox = ({
  selected,
  setSelected,
  placeholder = "Select assets...",
  className,
  fullWidth = false,
  onAssetSelect,
  onAssetDeselect,
  onAllSelect,
  onAllDeselect,
  assets: propAssets,
  fetchAssets: propFetchAssets,
}: AssetsComboboxProps) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<string | null>(null);
  
  const { 
    assets: contextAssets, 
    loading, 
    error, 
    refresh, 
    typeFilters,
    searchAssets,
  } = useAssets();
  
  // Use assets from props if provided, otherwise use from context
  const assets = propAssets || contextAssets;
  

  
  // Filter assets based on search query and type filter
  const filteredAssets = React.useMemo(() => {
    let result = [...assets];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(asset => 
        asset.name.toLowerCase().includes(query) ||
        (asset.description && asset.description.toLowerCase().includes(query))
      );
    }
    
    if (activeFilter) {
      result = result.filter(asset => asset.type === activeFilter);
    }
    
    return result;
  }, [assets, searchQuery, activeFilter]);

  // Toggle asset selection
  const handleSelect = React.useCallback((assetId: string) => {
    setSelected((prev: string[]) => {
      const newSelected = [...prev];
      const index = newSelected.indexOf(assetId);
      
      if (index > -1) {
        // Remove if already selected
        newSelected.splice(index, 1);
        onAssetDeselect?.(assetId);
      } else {
        // Add if not selected
        newSelected.push(assetId);
        onAssetSelect?.(assetId);
      }
      
      return newSelected;
    });
  }, [onAssetDeselect, onAssetSelect, setSelected]);
  
  // Check if an asset is selected
  const isSelected = React.useCallback((assetId: string) => 
    selected.includes(assetId)
  , [selected]);
  
  // Handle deselect all
  const handleDeselectAll = React.useCallback(() => {
    setSelected([]);
    onAllDeselect?.();
  }, [onAllDeselect, setSelected]);
  
  // Handle select all
  const handleSelectAll = React.useCallback(() => {
    const allAssetIds = filteredAssets.map(asset => asset.id);
    
    if (selected.length === allAssetIds.length) {
      // Deselect all
      handleDeselectAll();
    } else {
      // Select all
      setSelected(allAssetIds);
      onAllSelect?.();
    }
  }, [filteredAssets, selected.length, onAllSelect, setSelected, handleDeselectAll]);
  
  // Alias for handleSelect to match the toggleAsset name used in the template
  const toggleAsset = handleSelect;
  
  // Get selected asset objects
  const selectedAssets = React.useMemo(
    () => assets.filter(a => selected.includes(a.id)),
    [assets, selected]
  );
  
  return (
    <div className={cn("relative", fullWidth ? 'w-full' : 'w-[400px]', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              selected.length > 0 ? "h-auto min-h-8 py-1" : "h-8"
            )}
          >
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {selected.length > 0 ? (
                selected.map(id => {
                  const asset = assets.find(a => a.id === id);
                  if (!asset) return null;
                  
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm"
                    >
                      <span>{asset.name}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleSelect(id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSelect(id);
                          }
                        }}
                        className="opacity-50 hover:opacity-100 cursor-pointer"
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </div>
                  );
                })
              ) : (
                <span className="text-muted-foreground">{placeholder ? placeholder : 'No assets selected'}</span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <div className="flex items-center border-b">
              {/* <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" /> */}
              <CommandInput
                placeholder="Search assets..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="border-0 focus:ring-0 focus-visible:ring-0"
              />
              {selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={handleDeselectAll}
                >
                  Clear all
                </Button>
              )}
            </div>
            
            <CommandList>
              <div className="flex items-center border-b px-3 py-1.5 text-xs text-muted-foreground">
                <span className="mr-2">Filter by type:</span>
                <div className="flex flex-wrap gap-1">
                  <Badge
                    variant={!activeFilter ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer",
                      !activeFilter && "bg-primary/10 text-primary"
                    )}
                    onClick={() => setActiveFilter("")}
                  >
                    All
                  </Badge>
                  {typeFilters.map((type) => (
                    <Badge
                      key={type}
                      variant={activeFilter === type ? "default" : "outline"}
                      className={cn(
                        "cursor-pointer",
                        activeFilter === type && "bg-primary/10 text-primary"
                      )}
                      onClick={() => setActiveFilter(type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
                <div className="ml-auto">
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={handleSelectAll}
                  >
                    {selected.length === filteredAssets.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading assets...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Error loading assets: {error.message}
                </div>
              ) : assets.length === 0 ? (
                <CommandEmpty>No assets found.</CommandEmpty>
              ) : (
                <CommandGroup className="max-h-[300px] overflow-y-auto">
                  {filteredAssets.map((asset) => {
                    const isSelected = selected.includes(asset.id);
                    return (
                      <CommandItem
                        key={asset.id}
                        value={asset.id}
                        onSelect={() => handleSelect(asset.id)}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}>
                          <Check className={"h-4 w-4"} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{asset.name}</span>
                            {asset.type && (
                              <Badge variant="outline" className="text-xs">
                                {asset.type}
                              </Badge>
                            )}
                          </div>
                          {asset.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {asset.description}
                            </p>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AssetsCombobox;
